import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FiSave, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const EditSong = () => {
  const { songID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [editingSong, setEditingSong] = useState(null);
  const [editedSongData, setEditedSongData] = useState(null);
  const [activeTab, setActiveTab] = useState("arabic");
  const [userRole, setUserRole] = useState("");
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [saveStatus, setSaveStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });
  const [reportDetails, setReportDetails] = useState(null);
  const [reportNote, setReportNote] = useState(""); // State to store the report's note

  // Parse query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const partType = params.get("partType");
    const lineIndex = params.get("lineIndex");
    const verseIndex = params.get("verseIndex");
    const reportId = params.get("reportId");
    const language = params.get("language") || "arabic";

    if (partType && lineIndex !== null) {
      setReportDetails({
        partType,
        lineIndex: parseInt(lineIndex),
        verseIndex: verseIndex ? parseInt(verseIndex) : null,
        reportId,
        language,
      });
      setActiveTab(
        language === "franco"
          ? "franco"
          : language === "english"
          ? "english"
          : language === "spanish"
          ? "spanish"
          : "arabic"
      );
    }
  }, [location.search]);

  // Fetch report note if reportId exists
  useEffect(() => {
    const fetchReportNote = async () => {
      if (reportDetails?.reportId) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/reports/${reportDetails.reportId}`
          );
          setReportNote(response.data.note || "No note provided.");
        } catch (error) {
          console.error("Error fetching report note:", error);
          setReportNote("Failed to load report note.");
        }
      }
    };

    fetchReportNote();
  }, [reportDetails?.reportId]);

  // Admin check
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const role = localStorage.getItem("adminRole");
    if (!token || !["admin", "super user"].includes(role)) {
      navigate("/admin");
    } else {
      setUserRole(role);
      if (role === "admin") {
        fetchAdmins();
      }
    }
  }, [navigate]);

  const fetchAdmins = async (page = 1) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/users?page=${page}&limit=10`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const mappedAdmins = response.data.map((admin) => ({
        id: admin._id,
        name: admin.username,
        displayEmail: admin.username.toLowerCase() + "@praisesawa.com",
        email: admin.username.toLowerCase() + "@praisesawa.com",
        role: admin.role,
        employment: admin.role === "admin" ? "Full Access" : "Limited Access",
        status: "Active",
        note: admin.role === "admin" ? "System Admin" : "Content Manager",
      }));
      setAdmins(mappedAdmins);
      setTotalPages(Math.ceil(response.data.length / 10));
      setCurrentPage(page);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        setError(
          err.response?.data?.message || t("adminSettings.error.fetchAdmins")
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/logout`);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminRole");
      navigate("/admin");
      window.location.reload();
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // Fetch song data
  useEffect(() => {
    const fetchSongData = async () => {
      try {
        setIsLoading(true);
        const fullSongResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongBySongID/${songID}`
        );
        if (!fullSongResponse.ok) {
          throw new Error(`HTTP error! status: ${fullSongResponse.status}`);
        }
        const song = await fullSongResponse.json();
        setEditingSong(song);

        const francoResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/FrankSongs/getFrankSongBySongID/${songID}`
        );
        const francoSong = await francoResponse.json();

        const englishTranslation = {
          title: song.englishTranslatedTitle || "",
          chorus: song.englishTranslatedChorus || [],
          verses: song.englishTranslatedVerses || [],
        };

        const spanishTranslation = {
          title: song.spanishTranslatedTitle || "",
          chorus: song.spanishTranslatedChorus || [],
          verses: song.spanishTranslatedVerses || [],
        };

        setEditedSongData({
          title: song.title,
          chorus: [...song.chorus],
          verses: JSON.parse(JSON.stringify(song.verses)),
          en: englishTranslation,
          es: spanishTranslation,
          franco: francoSong || { title: "", chorus: [], verses: [] },
        });
      } catch (error) {
        console.error("Error fetching song data:", error);
        setSaveStatus({ loading: false, error: error.message, success: false });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSongData();
  }, [songID]);

  // Update report status
  const updateReportStatus = async (status) => {
    if (!reportDetails?.reportId) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/reports/${reportDetails.reportId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status }),
        }
      );
      if (!response.ok) {
        throw new Error(
          `HTTP error updating report status! status: ${response.status}`
        );
      }
    } catch (error) {
      console.error("Error updating report status:", error);
      setSaveStatus({ loading: false, error: error.message, success: false });
    }
  };

  const handleSaveSong = async () => {
    if (!editingSong || !editedSongData) return;

    setSaveStatus({ loading: true, error: null, success: false });

    try {
      // Update Arabic song
      const arbResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/updateSong/${editingSong.songID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            title: editedSongData.title,
            chorus: editedSongData.chorus,
            verses: editedSongData.verses,
            en: editedSongData.en,
            es: editedSongData.es,
          }),
        }
      );

      if (!arbResponse.ok) {
        throw new Error(
          `HTTP error updating Arabic song! status: ${arbResponse.status}`
        );
      }

      // Update Franco song
      const francoResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/FrankSongs/updateSong/${editingSong.songID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editedSongData.franco),
        }
      );

      if (!francoResponse.ok) {
        throw new Error(
          `HTTP error updating Franco song! status: ${francoResponse.status}`
        );
      }

      // Get userId from adminToken
      const token = localStorage.getItem("adminToken");
      const decodedToken = jwtDecode(token);
      const userId = decodedToken.id || decodedToken.sub;

      // Save song edit record
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/song-edits`,
        {
          songId: editingSong.songID,
          userId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update report status if applicable
      await updateReportStatus("resolved");

      setSaveStatus({ loading: false, error: null, success: true });

      setTimeout(() => {
        navigate("/admin");
      }, 1500);
    } catch (error) {
      console.error("Error updating songs or saving edit:", error);
      setSaveStatus({ loading: false, error: error.message, success: false });
    }
  };

  const handleDismissReport = async () => {
    setSaveStatus({ loading: true, error: null, success: false });
    try {
      await updateReportStatus("dismissed");
      setSaveStatus({ loading: false, error: null, success: true });
      setTimeout(() => {
        navigate("/admin");
      }, 1500);
    } catch (error) {
      console.error("Error dismissing report:", error);
      setSaveStatus({ loading: false, error: error.message, success: false });
    }
  };

  const handleCancelEdit = () => {
    navigate("/admin");
  };

  const handleInputChange = (field, value) => {
    setEditedSongData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChorusChange = (index, value) => {
    setEditedSongData((prev) => {
      if (!prev) return prev;

      if (activeTab === "arabic") {
        const updatedChorus = [...prev.chorus];
        updatedChorus[index] = value;
        return { ...prev, chorus: updatedChorus };
      } else if (activeTab === "english") {
        const updatedChorus = [...(prev.en?.chorus || [])];
        updatedChorus[index] = value;
        return { ...prev, en: { ...prev.en, chorus: updatedChorus } };
      } else if (activeTab === "spanish") {
        const updatedChorus = [...(prev.es?.chorus || [])];
        updatedChorus[index] = value;
        return { ...prev, es: { ...prev.es, chorus: updatedChorus } };
      } else if (activeTab === "franco") {
        const updatedChorus = [...(prev.franco?.chorus || [])];
        updatedChorus[index] = value;
        return { ...prev, franco: { ...prev.franco, chorus: updatedChorus } };
      }
      return prev;
    });
  };

  const handleVerseChange = (verseIndex, lineIndex, value) => {
    setEditedSongData((prev) => {
      if (!prev) return prev;

      if (activeTab === "arabic") {
        const updatedVerses = JSON.parse(JSON.stringify(prev.verses));
        updatedVerses[verseIndex][lineIndex] = value;
        return { ...prev, verses: updatedVerses };
      } else if (activeTab === "english") {
        const updatedVerses = JSON.parse(JSON.stringify(prev.en?.verses || []));
        updatedVerses[verseIndex][lineIndex] = value;
        return { ...prev, en: { ...prev.en, verses: updatedVerses } };
      } else if (activeTab === "spanish") {
        const updatedVerses = JSON.parse(JSON.stringify(prev.es?.verses || []));
        updatedVerses[verseIndex][lineIndex] = value;
        return { ...prev, es: { ...prev.es, verses: updatedVerses } };
      } else if (activeTab === "franco") {
        const updatedVerses = JSON.parse(
          JSON.stringify(prev.franco?.verses || [])
        );
        updatedVerses[verseIndex][lineIndex] = value;
        return { ...prev, franco: { ...prev.franco, verses: updatedVerses } };
      }
      return prev;
    });
  };

  const changeTab = (tabName) => {
    setActiveTab(tabName);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff4d4d]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent text-black">
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div>
              <h4 className="text-2xl font-bold bg-black bg-clip-text text-transparent">
                {t("admin.editSong")}
              </h4>
              <p className="text-gray-500 mt-1">
                {editedSongData?.title || ""}
              </p>
              {reportDetails && (
                <p
                  className="text-gray-500 mt-1"
                  dir={i18n.language === "ar" ? "rtl" : "ltr"}
                >
                  {t("admin.edit")} {/* Translation for "Editing" */}{" "}
                  {reportDetails.partType === "chorus"
                    ? t("admin.chorus") // "Chorus"
                    : t("admin.verse")}{" "}
                  {/* "Verse" */}{" "}
                  {t(
                    reportDetails.partType === "chorus"
                      ? "admin.chorusLine" // "Chorus Line"
                      : "admin.verseLine" // "Verse Line"
                  )}{" "}
                  {reportDetails.lineIndex + 1}
                  {reportDetails.partType === "verse" &&
                    ` (${t("admin.verse")} ${reportDetails.verseIndex + 1})`}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSaveSong}
                disabled={saveStatus.loading}
                className={`px-5 py-2.5 rounded-lg flex items-center shadow-md gap-2 ${
                  saveStatus.loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                } text-white transition-all duration-300 transform hover:scale-105`}
              >
                {saveStatus.loading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t("admin.saving")}
                  </span>
                ) : (
                  <>
                    <FiSave
                      className={`${i18n.language === "ar" ? "ml-2" : "mr-2"}`}
                    />
                    {t("admin.save")} ({t("adminSettings.status.resolved")})
                  </>
                )}
              </button>
              <button
                onClick={reportDetails ? handleDismissReport : handleCancelEdit}
                disabled={saveStatus.loading}
                className={`px-5 py-2.5 rounded-lg flex items-center gap-2 shadow-md ${
                  saveStatus.loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                } text-white transition-all duration-300 transform hover:scale-105`}
              >
                <FiX
                  className={`${i18n.language === "ar" ? "ml-2" : "mr-2"}`}
                />
                {reportDetails ? t("report.dismiss") : t("admin.cancel")}
              </button>
            </div>
          </div>

          {saveStatus.success && (
            <div className="mb-6 p-4 bg-green-100 border-l-4 border-green-500 text-green-700 rounded-md flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                ></path>
              </svg>
              {t("admin.saveSuccess")}
            </div>
          )}

          {saveStatus.error && (
            <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-md flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                ></path>
              </svg>
              {saveStatus.error}
            </div>
          )}

          <div className="mb-6 border-b border-gray-200">
            <ul
              className="flex flex-wrap -mb-px text-sm font-medium text-center"
              role="tablist"
            >
              <li className="mr-2" role="presentation">
                <button
                  className={`inline-block p-4 border-b-2 rounded-t-lg hover:text-gray-600 hover:border-gray-300 ${
                    activeTab === "arabic"
                      ? "border-[#ff4d4d] text-[#ff4d4d]"
                      : "border-transparent text-gray-500"
                  }`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "arabic"}
                  onClick={() => changeTab("arabic")}
                >
                  {t("admin.arabicVersion")}
                </button>
              </li>
              <li className="mr-2" role="presentation">
                <button
                  className={`inline-block p-4 border-b-2 rounded-t-lg hover:text-gray-600 hover:border-gray-300 ${
                    activeTab === "english"
                      ? "border-[#ff4d4d] text-[#ff4d4d]"
                      : "border-transparent text-gray-500"
                  }`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "english"}
                  onClick={() => changeTab("english")}
                >
                  {t("admin.englishTranslation")}
                </button>
              </li>
              <li className="mr-2" role="presentation">
                <button
                  className={`inline-block p-4 border-b-2 rounded-t-lg hover:text-gray-600 hover:border-gray-300 ${
                    activeTab === "spanish"
                      ? "border-[#ff4d4d] text-[#ff4d4d]"
                      : "border-transparent text-gray-500"
                  }`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "spanish"}
                  onClick={() => changeTab("spanish")}
                >
                  {t("admin.spanishTranslation")}
                </button>
              </li>
              <li className="mr-2" role="presentation">
                <button
                  className={`inline-block p-4 border-b-2 rounded-t-lg hover:text-gray-600 hover:border-gray-300 ${
                    activeTab === "franco"
                      ? "border-[#ff4d4d] text-[#ff4d4d]"
                      : "border-transparent text-gray-500"
                  }`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "franco"}
                  onClick={() => changeTab("franco")}
                >
                  {t("admin.francoVersion")}
                </button>
              </li>
            </ul>
          </div>

          <div className="space-y-6">
            {activeTab === "arabic" && (
              <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.title")}
                    </label>
                    <input
                      type="text"
                      value={editedSongData?.title || ""}
                      onChange={(e) =>
                        handleInputChange("title", e.target.value)
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md"
                      placeholder={t("admin.enterTitle")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.chorus")}
                    </label>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {editedSongData?.chorus?.map((line, index) => (
                        <div key={`chorus-${index}`} className="mb-3">
                          <div className="flex items-center mb-1">
                            <span
                              className={`text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5 ${
                                i18n.language === "ar" ? "ml-2" : "mr-2"
                              }`}
                            >
                              {index + 1}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t("admin.chorusLine")}
                            </span>
                          </div>
                          <textarea
                            value={line}
                            onChange={(e) =>
                              handleChorusChange(index, e.target.value)
                            }
                            className={`min-h-40 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md ${
                              reportDetails?.partType === "chorus" &&
                              reportDetails?.lineIndex === index &&
                              reportDetails?.language === "english"
                                ? "border-2 border-red-500 bg-red-50"
                                : ""
                            }`}
                            rows="2"
                          />
                          {reportDetails?.partType === "chorus" &&
                            reportDetails?.lineIndex === index &&
                            reportDetails?.language === "english" && (
                              <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-md">
                                <span className="font-medium">
                                  {t("report.note")}:
                                </span>{" "}
                                {reportNote}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.verses")}
                    </label>
                    <div className="space-y-4">
                      {editedSongData?.verses?.map((verse, verseIndex) => (
                        <div
                          key={`verse-${verseIndex}`}
                          className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-sm font-medium bg-black text-white rounded-full px-3 py-1">
                              {verseIndex + 1}
                            </span>
                            <span className="text-sm font-medium text-gray-700">
                              {t("admin.verse")}
                            </span>
                          </div>
                          {verse.map((line, lineIndex) => (
                            <div
                              key={`verse-${verseIndex}-line-${lineIndex}`}
                              className="mb-3"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5 mr-2">
                                  {lineIndex + 1}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {t("admin.verseLine")}
                                </span>
                              </div>
                              <textarea
                                value={line}
                                onChange={(e) =>
                                  handleVerseChange(
                                    verseIndex,
                                    lineIndex,
                                    e.target.value
                                  )
                                }
                                className={`min-h-40 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md ${
                                  reportDetails?.partType === "verse" &&
                                  reportDetails?.verseIndex === verseIndex &&
                                  reportDetails?.lineIndex === lineIndex &&
                                  reportDetails?.language === "arabic"
                                    ? "border-2 border-red-500 bg-red-50"
                                    : ""
                                }`}
                                rows="2"
                                dir="rtl"
                              />
                              {reportDetails?.partType === "verse" &&
                                reportDetails?.verseIndex === verseIndex &&
                                reportDetails?.lineIndex === lineIndex &&
                                reportDetails?.language === "arabic" && (
                                  <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-md">
                                    <span className="font-medium">
                                      {t("report.note")}:
                                    </span>{" "}
                                    {reportNote}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "english" && (
              <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.title")}
                    </label>
                    <input
                      type="text"
                      value={editedSongData?.en?.title || ""}
                      onChange={(e) =>
                        handleInputChange("en", {
                          ...editedSongData.en,
                          title: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md"
                      placeholder={
                        t("display.english") + " " + t("admin.title")
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.chorus")}
                    </label>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {editedSongData?.en?.chorus?.map((line, index) => (
                        <div key={`en-chorus-${index}`} className="mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5">
                              {index + 1}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t("admin.chorusLine")}
                            </span>
                          </div>
                          <textarea
                            value={line}
                            onChange={(e) =>
                              handleChorusChange(index, e.target.value)
                            }
                            className={`min-h-40 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md ${
                              reportDetails?.partType === "chorus" &&
                              reportDetails?.lineIndex === index &&
                              reportDetails?.language === "english"
                                ? "border-2 border-red-500 bg-red-50"
                                : ""
                            }`}
                            rows="2"
                          />
                          {reportDetails?.partType === "chorus" &&
                            reportDetails?.lineIndex === index &&
                            reportDetails?.language === "english" && (
                              <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-md">
                                <span className="font-medium">
                                  {t("report.note")}:
                                </span>{" "}
                                {reportNote}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.verses")}
                    </label>
                    <div className="space-y-4">
                      {editedSongData?.en?.verses?.map((verse, verseIndex) => (
                        <div
                          key={`en-verse-${verseIndex}`}
                          className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-sm font-medium bg-black text-white rounded-full px-3 py-1">
                              {verseIndex + 1}
                            </span>
                            <span className="text-sm font-medium text-gray-700">
                              {t("admin.verse")}
                            </span>
                          </div>
                          {verse.map((line, lineIndex) => (
                            <div
                              key={`en-verse-${verseIndex}-line-${lineIndex}`}
                              className="mb-3"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5">
                                  {lineIndex + 1}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {t("admin.verseLine")}
                                </span>
                              </div>
                              <textarea
                                value={line}
                                onChange={(e) =>
                                  handleVerseChange(
                                    verseIndex,
                                    lineIndex,
                                    e.target.value
                                  )
                                }
                                className={`min-h-40 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md ${
                                  reportDetails?.partType === "verse" &&
                                  reportDetails?.verseIndex === verseIndex &&
                                  reportDetails?.lineIndex === lineIndex &&
                                  reportDetails?.language === "english"
                                    ? "border-2 border-red-500 bg-red-50"
                                    : ""
                                }`}
                                rows="2"
                              />
                              {reportDetails?.partType === "verse" &&
                                reportDetails?.verseIndex === verseIndex &&
                                reportDetails?.lineIndex === lineIndex &&
                                reportDetails?.language === "english" && (
                                  <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-md">
                                    <span className="font-medium">
                                      {t("report.note")}:
                                    </span>{" "}
                                    {reportNote}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "spanish" && (
              <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.title")}
                    </label>
                    <input
                      type="text"
                      value={editedSongData?.es?.title || ""}
                      onChange={(e) =>
                        handleInputChange("es", {
                          ...editedSongData.es,
                          title: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md"
                      placeholder={
                        t("display.spanish") + " " + t("admin.title")
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.chorus")}
                    </label>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {editedSongData?.es?.chorus?.map((line, index) => (
                        <div key={`es-chorus-${index}`} className="mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5">
                              {index + 1}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t("admin.chorusLine")}
                            </span>
                          </div>
                          <textarea
                            value={line}
                            onChange={(e) =>
                              handleChorusChange(index, e.target.value)
                            }
                            className={`min-h-40 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md ${
                              reportDetails?.partType === "chorus" &&
                              reportDetails?.lineIndex === index &&
                              reportDetails?.language === "spanish"
                                ? "border-2 border-red-500 bg-red-50"
                                : ""
                            }`}
                            rows="2"
                          />
                          {reportDetails?.partType === "chorus" &&
                            reportDetails?.lineIndex === index &&
                            reportDetails?.language === "spanish" && (
                              <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-md">
                                <span className="font-medium">
                                  {t("report.note")}:
                                </span>{" "}
                                {reportNote}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.verses")}
                    </label>
                    <div className="space-y-4">
                      {editedSongData?.es?.verses?.map((verse, verseIndex) => (
                        <div
                          key={`es-verse-${verseIndex}`}
                          className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center mb-3">
                            <span className="text-sm font-medium bg-black text-white rounded-full px-3 py-1">
                              {verseIndex + 1}
                            </span>
                            <span className="text-sm font-medium text-gray-700">
                              {t("admin.verse")}
                            </span>
                          </div>
                          {verse.map((line, lineIndex) => (
                            <div
                              key={`es-verse-${verseIndex}-line-${lineIndex}`}
                              className="mb-3"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5 mr-2">
                                  {lineIndex + 1}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {t("admin.verseLine")}
                                </span>
                              </div>
                              <textarea
                                value={line}
                                onChange={(e) =>
                                  handleVerseChange(
                                    verseIndex,
                                    lineIndex,
                                    e.target.value
                                  )
                                }
                                className={`min-h-40 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md ${
                                  reportDetails?.partType === "verse" &&
                                  reportDetails?.verseIndex === verseIndex &&
                                  reportDetails?.lineIndex === lineIndex &&
                                  reportDetails?.language === "spanish"
                                    ? "border-2 border-red-500 bg-red-50"
                                    : ""
                                }`}
                                rows="2"
                              />
                              {reportDetails?.partType === "verse" &&
                                reportDetails?.verseIndex === verseIndex &&
                                reportDetails?.lineIndex === lineIndex &&
                                reportDetails?.language === "spanish" && (
                                  <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-md">
                                    <span className="font-medium">
                                      {t("report.note")}:
                                    </span>{" "}
                                    {reportNote}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "franco" && (
              <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.title")}
                    </label>
                    <input
                      type="text"
                      value={editedSongData?.franco?.title || ""}
                      onChange={(e) =>
                        handleInputChange("franco", {
                          ...editedSongData.franco,
                          title: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md"
                      placeholder={t("display.franco") + " " + t("admin.title")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.chorus")}
                    </label>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {editedSongData?.franco?.chorus?.map((line, index) => (
                        <div key={`franco-chorus-${index}`} className="mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5 mr-2">
                              {index + 1}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t("admin.chorusLine")}
                            </span>
                          </div>
                          <textarea
                            value={line}
                            onChange={(e) =>
                              handleChorusChange(index, e.target.value)
                            }
                            className={`min-h-40 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md ${
                              reportDetails?.partType === "chorus" &&
                              reportDetails?.lineIndex === index &&
                              reportDetails?.language === "franco"
                                ? "border-2 border-red-500 bg-red-50"
                                : ""
                            }`}
                            rows="2"
                          />
                          {reportDetails?.partType === "chorus" &&
                            reportDetails?.lineIndex === index &&
                            reportDetails?.language === "franco" && (
                              <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-md">
                                <span className="font-medium">
                                  {t("report.note")}:
                                </span>{" "}
                                {reportNote}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("admin.verses")}
                    </label>
                    <div className="space-y-4">
                      {editedSongData?.franco?.verses?.map(
                        (verse, verseIndex) => (
                          <div
                            key={`franco-verse-${verseIndex}`}
                            className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center mb-3">
                              <span className="text-sm font-medium bg-black text-white rounded-full px-3 py-1 mr-2">
                                {verseIndex + 1}
                              </span>
                              <span className="text-sm font-medium text-gray-700">
                                {t("admin.verse")}
                              </span>
                            </div>
                            {verse.map((line, lineIndex) => (
                              <div
                                key={`franco-verse-${verseIndex}-line-${lineIndex}`}
                                className="mb-3"
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5 mr-2">
                                    {lineIndex + 1}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {t("admin.verseLine")}
                                  </span>
                                </div>
                                <textarea
                                  value={line}
                                  onChange={(e) =>
                                    handleVerseChange(
                                      verseIndex,
                                      lineIndex,
                                      e.target.value
                                    )
                                  }
                                  className={`min-h-40 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4d4d] focus:border-[#ff4d4d] shadow-sm transition-all duration-200 hover:shadow-md ${
                                    reportDetails?.partType === "verse" &&
                                    reportDetails?.verseIndex === verseIndex &&
                                    reportDetails?.lineIndex === lineIndex &&
                                    reportDetails?.language === "franco"
                                      ? "border-2 border-red-500 bg-red-50"
                                      : ""
                                  }`}
                                  rows="2"
                                />
                                {reportDetails?.partType === "verse" &&
                                  reportDetails?.verseIndex === verseIndex &&
                                  reportDetails?.lineIndex === lineIndex &&
                                  reportDetails?.language === "franco" && (
                                    <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-md">
                                      <span className="font-medium">
                                        {t("report.note")}:
                                      </span>{" "}
                                      {reportNote}
                                    </div>
                                  )}
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditSong;